/**
 * Route Configuration with Loaders (SSR-Ready)
 *
 * This file defines all application routes using React Router v7's data API.
 * Loaders run before route components render, enabling:
 * - Server-side rendering (SSR)
 * - Data prefetching
 * - Authentication guards
 * - Permission checks
 *
 * Architecture:
 * - Public routes: No authentication required
 * - Protected routes: Require authentication (redirect to /login)
 * - Admin routes: Require specific roles/permissions
 *
 * SSR DEPLOYMENT OPTIONS:
 * ────────────────────────────────────────────────────────────────
 * Option 1: Remix (Recommended)
 *   - Native React Router v7 support, zero-config SSR
 *   - Install: npm install @remix-run/node @remix-run/react
 *   - Convert routes to Remix route modules
 *   - Deploy to Vercel, Fly.io, or Railway
 *
 * Option 2: Custom Express Server
 *   - Add SSR rendering to existing API server
 *   - Use createStaticHandler from react-router-dom/server
 *   - Full control over SSR logic
 *
 * Option 3: Static Site Generation (SSG)
 *   - Pre-render pages at build time
 *   - Deploy to S3/CloudFront (no server required)
 *   - Best for mostly-static content
 */

import { redirect } from 'react-router-dom';
import * as authService from './services/authService';
import { searchGuides, parseSearchParams } from './services/searchService';
import apiRequest from './services/api';
import { clearTokens, isTokenLikelyValid } from './services/tokenRefreshInterceptor';

// Environment variables
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

// Page components
import HomePage from './pages/public/HomePage';
import ComingSoonPage from './pages/public/ComingSoonPage';
import GuideProfilePage from './pages/public/GuideProfilePage';
import GuideSignupFlow from './components/guide/signup/GuideSignupFlow';
import GuideVerification from './pages/guide/GuideVerification';
import GuideSignupSuccess from './pages/public/GuideSignupSuccess';
import ABTestDashboard from './components/admin/ABTestDashboard';
import ReferralDashboard from './components/admin/ReferralDashboard';
import ErrorsDashboard from './components/admin/ErrorsDashboard';
import ServicesUsageDashboard from './components/admin/ServicesUsageDashboard';
import MessageQueueDashboard from './components/admin/MessageQueueDashboard';
import SpeciesCatalogAdmin from './pages/admin/SpeciesCatalogAdmin';
import GearCatalogAdmin from './pages/admin/GearCatalogAdmin';
import GuideApprovalQueue from './pages/admin/GuideApprovalQueue';
import PlatformAnalyticsDashboard from './pages/admin/PlatformAnalyticsDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminSettings from './pages/admin/AdminSettings';
import OnCallOverview from './pages/admin/OnCallOverview';
import SystemHealthDashboard from './pages/admin/SystemHealthDashboard';
import UserManagement from './pages/admin/UserManagement';
import GuideDashboard from './pages/guide/GuideDashboard';
import GuideBookings from './pages/guide/GuideBookings';
import GuideBookingDetail from './pages/guide/GuideBookingDetail';
import GuideReviews from './pages/guide/GuideReviews';
import GuideExperiences from './pages/guide/GuideExperiences';
import GuideAvailabilityPage from './pages/guide/GuideAvailabilityPage';
import CreateExperiencePage from './pages/guide/CreateExperiencePage';
import EditExperiencePage from './pages/guide/EditExperiencePage';
import ExperiencePricingPage from './pages/guide/ExperiencePricingPage';
import ExperienceDetail from './pages/public/ExperienceDetail';
import ExperienceDetailStatic from './pages/public/ExperienceDetailStatic';
import RevenueDashboard from './pages/guide/RevenueDashboard';
import TransactionHistory from './pages/guide/TransactionHistory';
import ReferralTracking from './pages/guide/ReferralTracking';
import LedgerExportPage from './pages/guide/LedgerExportPage';
import PaymentSettings from './pages/guide/PaymentSettings';
import TaxDocuments from './pages/guide/TaxDocuments';
import AccountingPage from './pages/guide/AccountingPage';
import Messages from './pages/guide/Messages';
import ProfileEditor from './pages/guide/ProfileEditor';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import CustomerTrips from './pages/customer/CustomerTrips';
import CustomerTripDetail from './pages/customer/CustomerTripDetail';
import CustomerMessages from './pages/customer/CustomerMessages';
import CustomerReviews from './pages/customer/CustomerReviews';
import CustomerReviewWizard from './pages/customer/CustomerReviewWizard';
import CustomerReviewEdit from './pages/customer/CustomerReviewEdit';
import CustomerFavorites from './pages/customer/CustomerFavorites';
import PaymentHistory from './pages/customer/PaymentHistory';
import NotificationsPage from './pages/notifications/NotificationsPage';
import AccountSettings from './pages/account/AccountSettings';
import UnauthorizedPage from './pages/auth/UnauthorizedPage';
import ServiceUnavailablePage from './pages/public/ServiceUnavailablePage';
import RateLimitPage from './pages/public/RateLimitPage';
import LoginPage from './pages/auth/LoginPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import SetPasswordPage from './pages/account/SetPasswordPage';
import DeletionStatusPage from './pages/public/DeletionStatusPage';
import ReclaimAccountPage from './pages/account/ReclaimAccountPage';
import AuthCallbackPage from './pages/auth/AuthCallbackPage';
import BookingFlowPage from './pages/booking/BookingFlowPage';
import BookingConfirmationPage from './pages/booking/BookingConfirmationPage';
import ConfirmEmailPreLaunchPage from './pages/auth/ConfirmEmailPreLaunchPage';
import SupportDashboard from './pages/support/SupportDashboard';
import SupportReports from './pages/support/SupportReports';
import SupportTicketDetail from './pages/support/SupportTicketDetail';
import ReviewModerationQueue from './pages/admin/ReviewModerationQueue';
import BookingManagement from './pages/admin/BookingManagement';
import CancellationManagement from './pages/admin/CancellationManagement';
import DisputeManagement from './pages/admin/DisputeManagement';
import DlqMonitoring from './pages/admin/DlqMonitoring';
import RefundManagement from './pages/admin/RefundManagement';
import StuckRefunds from './pages/admin/StuckRefunds';
import ExperienceManagement from './pages/admin/ExperienceManagement';
import LeadActionFeed from './pages/admin/LeadActionFeed';
import LeadManagement from './pages/admin/LeadManagement';
import MarketsPanel from './pages/admin/MarketsPanel';
import IntentCodePanel from './pages/admin/IntentCodePanel';
import EventsPanel from './pages/admin/EventsPanel';
import EventDetail from './pages/admin/EventDetail';
import CampaignManagement from './pages/admin/CampaignManagement';
import CampaignDetail from './pages/admin/CampaignDetail';
import IntentTypeManagement from './pages/admin/IntentTypeManagement';
import LegalDocumentManagement from './pages/admin/LegalDocumentManagement';
import SchedulingPanel from './pages/admin/SchedulingPanel';
import SchedulingOAuthCallback from './pages/admin/SchedulingOAuthCallback';
import AdminAccounting from './pages/admin/AdminAccounting';
import SchedulePage from './pages/public/SchedulePage';
import StaticPage from './pages/public/StaticPage';
import LegalDocumentPage from './pages/public/LegalDocumentPage';
import IntentResponsePage from './pages/public/IntentResponsePage';

// Loaders
import { staticPageLoader } from './loaders/staticPageLoader';

// Pre-launch config TTL cache (SCRUM-1462)
import { getPreLaunchConfig } from './utils/preLaunchConfigCache';
// Pre-launch market slug derivation (SCRUM-1463)
import { deriveMarketSlugFromPath } from './utils/preLaunchMarketSlug';

// Pre-launch exempt route wrapper (SCRUM-2005)
import PreLaunchExemptRoute from './components/layout/PreLaunchLayout';

// Error boundary
import ErrorBoundary from './components/error/ErrorBoundary';

// Loading fallback component for hydration
function HydrateFallback() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

// ============================================================================
// LOADER UTILITIES
// ============================================================================

/**
 * SCRUM-2124: singleton promise so concurrent loader calls (rapid navigation,
 * multiple tabs, React render races) share a single in-flight /auth/me request.
 * Reset on completion (success or failure) so the next call after settle does a
 * fresh fetch — matches the tokenRefreshInterceptor pattern.
 */
let inflightAuthPromise = null;

/**
 * Check if _user is authenticated
 * Returns _user object or null
 * ALWAYS validates token with API (single source of truth)
 */
export async function getAuthenticatedUser() {
  if (inflightAuthPromise) { return inflightAuthPromise; }

  inflightAuthPromise = (async () => {
    try {
      if (!authService.isAuthenticated()) {
        return null;
      }

      // CRITICAL: Always fetch fresh _user data from API
      // This validates the token and ensures we never use stale _user data
      // If token is expired, api.js will automatically refresh it
      const { user: _user } = await authService.getCurrentUser();
      return _user;
    } catch (error) {
      console.error('[Routes] Failed to get authenticated _user:', error);

      if (error?.status === 401) {
        clearTokens();
        window.dispatchEvent(new CustomEvent('auth:logout', {
          detail: { reason: 'token_validation_failed' }
        }));
      }

      return null;
    }
  })().finally(() => {
    inflightAuthPromise = null;
  });

  return inflightAuthPromise;
}

/** Test-only: reset the singleton promise. */
export function _resetAuthLoaderState() {
  inflightAuthPromise = null;
}

/**
 * Require authentication loader
 * Redirects to login if not authenticated
 * Returns _user object if authenticated
 */
async function requireAuthLoader({ request }) {
  const _user = await getAuthenticatedUser();

  if (!_user) {
    const url = new URL(request.url);
    // Preserve intended destination for redirect after login
    const redirectTo = url.pathname + url.search;
    return redirect(`/login?redirect=${encodeURIComponent(redirectTo)}`);
  }

  return { _user };
}

/**
 * Require specific role loader factory
 * Creates a loader that checks for required role
 *
 * @param {string|string[]} allowedRoles - Role(s) that can access this route
 * @returns {Function} Loader function
 */
function requireRoleLoader(allowedRoles) {
  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

  return async ({ request }) => {
    // First check authentication
    const { _user } = await requireAuthLoader({ request });

    // If user is null, requireAuthLoader should have redirected, but double-check
    if (!_user) {
      return redirect('/login?redirect=' + encodeURIComponent(new URL(request.url).pathname));
    }

    // Check role - support both multi-role (roles array) and legacy single role
    const userRoles = _user.roles || [];
    const hasRole = roles.some(role =>
      userRoles.includes(role) ||
      _user.primaryRole === role ||
      _user.user_type === role
    );

    if (!hasRole) {
      return redirect('/unauthorized');
    }

    return { _user };
  };
}

/**
 * Optional auth loader
 * Gets _user if authenticated, but doesn't redirect if not
 * Useful for pages that work for both auth and non-auth users
 */
async function optionalAuthLoader() {
  const _user = await getAuthenticatedUser();
  return { _user };
}

/**
 * Pre-launch protection wrapper
 * Wraps a loader to redirect unauthenticated users to /coming-soon during pre-launch
 *
 * @param {Function} loaderFn - The original loader function to wrap
 * @returns {Function} - Wrapped loader that checks pre-launch mode
 */
const PRE_LAUNCH_EXEMPT_PATHS = [
  '/guides/signup',
  '/guides/verification',
  '/guides/signup-success',
  '/i/',
];

function withPreLaunchProtection(loaderFn) {
  return async (args) => {
    // Derive marketSlug from request URL (AC-01/02, SCRUM-1463)
    // Priority: URL path segment → fallback to 'default' (global config)
    const pathname = args?.request ? new URL(args.request.url).pathname : '';
    const marketSlug = deriveMarketSlugFromPath(pathname) || 'default';

    // Check if in pre-launch mode — use TTL cache keyed by marketSlug (SCRUM-1462)
    let isPreLaunchMode = false;
    try {
      const configUrl = marketSlug === 'default'
        ? '/api/v1/pre-launch/config'
        : `/api/v1/pre-launch/config/${marketSlug}`;
      const preLaunchResponse = await getPreLaunchConfig(
        marketSlug,
        () => apiRequest(configUrl)
      );
      isPreLaunchMode = preLaunchResponse.success && preLaunchResponse.data?.enabled;
      console.debug('[PreLaunchProtection] gate evaluated:', {
        enabled: preLaunchResponse.data?.enabled,
        isPreLaunchMode,
      });
    } catch (error) {
      console.error('Failed to check pre-launch mode:', error);
    }

    // If pre-launch mode is enabled, check for a valid local token (no /auth/me call)
    if (isPreLaunchMode) {
      // SCRUM-2005: exempt paths bypass the redirect (signup + intent flows)
      const isExempt = PRE_LAUNCH_EXEMPT_PATHS.some(p => pathname.startsWith(p));
      if (!isExempt && !isTokenLikelyValid()) {
        return redirect('/coming-soon');
      }
    }

    // Pre-launch check passed (or not in pre-launch mode), call original loader
    return loaderFn(args);
  };
}

// ============================================================================
// ROUTE LOADERS
// ============================================================================

/**
 * Home page loader
 * Fetches search results based on URL params
 * Works for both authenticated and anonymous users
 * Pre-launch protection is handled by withPreLaunchProtection wrapper
 */
async function homeLoader({ request }) {
  const url = new URL(request.url);
  const searchParams = new URLSearchParams(url.search);

  // Get _user if authenticated (optional)
  const _user = await getAuthenticatedUser();

  // Parse search filters from URL
  const filters = parseSearchParams(searchParams);

  try {
    // Fetch search results
    const response = await searchGuides(filters);

    if (response.success && response.data) {
      return {
        _user,
        experiences: response.data.results || [],
        searchMetadata: {
          total: response.data.total,
          originalTotal: response.data.originalTotal,
          pagination: response.data.pagination,
          metadata: response.data.metadata
        }
      };
    }

    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Search error:', error);
    return {
      _user,
      experiences: [],
      searchMetadata: null,
      error: error.message || 'Failed to load search results'
    };
  }
}

/**
 * Guide profile page loader
 * Fetches guide profile and services
 */
async function guideProfileLoader({ params }) {
  const _user = await getAuthenticatedUser();

  try {
    const token = localStorage.getItem('accessToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(
      `${API_BASE_URL}/api/v1/guides/profiles/public/${params.guideId}`,
      { headers, credentials: 'include' }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return { _user, guideId: params.guideId, profile: null, error: 'Guide profile not found' };
      }
      throw new Error('Failed to fetch guide profile');
    }

    const result = await response.json();

    return {
      _user,
      guideId: params.guideId,
      profile: result.data,
      error: null,
    };
  } catch (error) {
    console.error('[Routes] Guide profile fetch failed:', error);
    return {
      _user,
      guideId: params.guideId,
      profile: null,
      error: error.message || 'Failed to load guide profile',
    };
  }
}

/**
 * Experience detail loader
 * Fetches experience data and determines if _user can edit
 * Shows edit mode if _user owns the experience, otherwise shows customer view
 */
async function experienceDetailLoader({ params }) {
  const _user = await getAuthenticatedUser();

  try {
    // Fetch experience details from API
    const token = localStorage.getItem('accessToken');
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/experiences/${params.experienceId}`, {
      headers,
      credentials: 'include' // Send cookies with request
    });

    if (!response.ok) {
      throw new Error('Failed to fetch experience details');
    }

    const result = await response.json();
    const experience = result.data;

    // Use category from API response, or default to 37 (Fishing)
    const categoryL1Id = experience.categoryId || 37;

    // Check ownership: _user can edit if they're a guide and own this experience
    let isOwner = false;
    if (_user && experience.guideId === _user.id) {
      isOwner = true;
    }

    return {
      _user,
      experienceId: params.experienceId,
      categoryL1Id,
      isOwner,
      experienceDetails: experience
    };
  } catch (error) {
    console.error('Error loading experience details:', error);

    // Fallback: return basic data without experience details
    return {
      _user,
      experienceId: params.experienceId,
      categoryL1Id: 37, // Default category
      isOwner: false,
      experienceDetails: null,
      error: error.message
    };
  }
}

/**
 * Admin dashboard loader
 * Requires super_admin or staff role
 * Checks pre-launch mode before authentication
 * Redirects unauthenticated users to /admin/login (SCRUM-1721)
 */
async function adminDashboardLoader({ request }) {
  // SCRUM-1778: Consume JWT from SAML ACS redirect before auth check.
  // Backend redirects to /admin/dashboard?token=... after IC SAML flow completes.
  // The token must be stored in localStorage before getAuthenticatedUser() runs.
  const url = new URL(request.url);
  const tokenParam = url.searchParams.get('token');

  if (tokenParam) {
    localStorage.setItem('accessToken', tokenParam);
    localStorage.setItem('tokenIssuedAt', String(Date.now()));

    // Notify AuthContext so it reloads user state
    window.dispatchEvent(new CustomEvent('auth:token-refreshed'));

    // Security: strip token from URL (prevents leaking JWT in browser history/referrer headers)
    return redirect(url.pathname);
  }

  // Check if in pre-launch mode
  let isPreLaunchMode = false;
  try {
    const preLaunchResponse = await apiRequest('/api/v1/pre-launch/config');
    isPreLaunchMode = preLaunchResponse.success && preLaunchResponse.data?.enabled;
  } catch (error) {
    console.error('Failed to check pre-launch mode:', error);
  }

  // If pre-launch mode is enabled, check for a valid local token (no /auth/me call)
  if (isPreLaunchMode && !isTokenLikelyValid()) {
    return redirect('/coming-soon');
  }

  // Check authentication — redirect to /admin/login (not /login) for admin routes (SCRUM-1721)
  const _user = await getAuthenticatedUser();

  if (!_user) {
    const url = new URL(request.url);
    const returnTo = url.pathname + url.search;
    return redirect(`/admin/login?returnTo=${encodeURIComponent(returnTo)}`);
  }

  // Check role — admin, super_admin, or staff required
  const userRoles = _user.roles || [];
  const allowedRoles = ['super_admin', 'admin', 'staff'];
  const hasRole = allowedRoles.some(role =>
    userRoles.includes(role) ||
    _user.primaryRole === role ||
    _user.user_type === role
  );

  if (!hasRole) {
    return redirect('/unauthorized');
  }

  return { _user };
}

/**
 * Guide dashboard loader
 * Requires guide role and fetches dashboard metrics from API
 * Checks pre-launch mode before authentication
 */
async function guideDashboardLoader({ request }) {
  // Check if in pre-launch mode
  let isPreLaunchMode = false;
  try {
    const preLaunchResponse = await apiRequest('/api/v1/pre-launch/config');
    isPreLaunchMode = preLaunchResponse.success && preLaunchResponse.data?.enabled;
  } catch (error) {
    console.error('Failed to check pre-launch mode:', error);
  }

  // If pre-launch mode is enabled, check for a valid local token (no /auth/me call)
  if (isPreLaunchMode && !isTokenLikelyValid()) {
    return redirect('/coming-soon');
  }

  // Check authentication and role
  const roleResult = await requireRoleLoader(['guide'])({ request });

  // If requireRoleLoader returned a redirect (Response), pass it through (SCRUM-1056)
  if (roleResult instanceof Response) {
    return roleResult;
  }

  const { _user } = roleResult;

  try {
    // Fetch dashboard overview metrics from API
    const overviewData = await apiRequest('/api/v1/dashboard/overview');

    return {
      _user,
      overview: overviewData.data,
    };
  } catch (error) {
    // If authentication error (401), redirect to login
    if (error.status === 401 || error.message?.includes('No token') || error.message?.includes('Invalid token')) {
      const url = new URL(request.url);
      const redirectTo = url.pathname + url.search;
      return redirect(`/login?redirect=${encodeURIComponent(redirectTo)}`);
    }

    // Redirect on 403 — customer on /guide/dashboard gets sent to /dashboard (SCRUM-1056)
    if (error.status === 403) {
      return redirect('/dashboard');
    }

    // For network/other errors, return empty data gracefully
    return {
      _user,
      overview: {},
    };
  }
}

/**
 * Customer dashboard loader
 * Requires customer role and fetches dashboard data from API
 * Checks pre-launch mode before authentication
 */
async function customerDashboardLoader({ request }) {
  // Check if in pre-launch mode
  let isPreLaunchMode = false;
  try {
    const preLaunchResponse = await apiRequest('/api/v1/pre-launch/config');
    isPreLaunchMode = preLaunchResponse.success && preLaunchResponse.data?.enabled;
  } catch (error) {
    console.error('Failed to check pre-launch mode:', error);
  }

  // If pre-launch mode is enabled, check for a valid local token (no /auth/me call)
  if (isPreLaunchMode && !isTokenLikelyValid()) {
    return redirect('/coming-soon');
  }

  // Check authentication and role
  const { _user } = await requireRoleLoader(['customer'])({ request });

  try {
    // Fetch customer dashboard data from API
    const dashboardData = await apiRequest('/api/v1/customer/dashboard');

    return {
      _user,
      dashboard: dashboardData.data,
    };
  } catch (error) {
    // If authentication error (401), redirect to login
    if (error.status === 401 || error.message?.includes('No token') || error.message?.includes('Invalid token')) {
      const url = new URL(request.url);
      const redirectTo = url.pathname + url.search;
      return redirect(`/login?redirect=${encodeURIComponent(redirectTo)}`);
    }

    // For other errors, return empty data
    return {
      _user,
      dashboard: {
        action_items: [],
        action_items_summary: {},
        upcoming_trips: [],
        statistics: {},
        favorite_guides: []
      },
    };
  }
}

/**
 * Errors dashboard loader
 * Fetches error statistics and recent errors
 * Checks pre-launch mode before authentication
 */
async function errorsDashboardLoader({ request }) {
  // Check if in pre-launch mode
  let isPreLaunchMode = false;
  try {
    const preLaunchResponse = await apiRequest('/api/v1/pre-launch/config');
    isPreLaunchMode = preLaunchResponse.success && preLaunchResponse.data?.enabled;
  } catch (error) {
    console.error('Failed to check pre-launch mode:', error);
  }

  // If pre-launch mode is enabled, check for a valid local token (no /auth/me call)
  if (isPreLaunchMode && !isTokenLikelyValid()) {
    return redirect('/coming-soon');
  }

  // Check authentication and role
  const { _user } = await requireRoleLoader(['super_admin', 'staff'])({ request });

  try {
    // Fetch error statistics
    const statsData = await apiRequest('/api/v1/errors/stats');

    // Fetch recent errors
    const errorsData = await apiRequest('/api/v1/errors?limit=100');

    return {
      _user,
      stats: statsData.data,
      errors: errorsData.data,
    };
  } catch (error) {
    // If authentication error (401), redirect to login
    if (error.status === 401 || error.message?.includes('No token') || error.message?.includes('Invalid token')) {
      const url = new URL(request.url);
      const redirectTo = url.pathname + url.search;
      return redirect(`/login?redirect=${encodeURIComponent(redirectTo)}`);
    }

    // For other errors, return empty data with error message
    return {
      _user,
      stats: {
        total_errors: 0,
        affected_users: 0,
        uncaught_errors: 0,
        route_errors: 0,
        api_errors: 0,
        production_errors: 0,
        development_errors: 0,
      },
      errors: {
        errors: [],
        total: 0,
        limit: 100,
        offset: 0,
        hasMore: false,
      },
      error: error.message,
    };
  }
}

/**
 * Catch-all loader for undefined routes
 * Redirects to /coming-soon if pre-launch mode is enabled
 * Otherwise redirects to home (404 handling)
 */
async function catchAllLoader() {
  // Check if in pre-launch mode
  let isPreLaunchMode = false;
  try {
    const preLaunchResponse = await apiRequest('/api/v1/pre-launch/config');
    isPreLaunchMode = preLaunchResponse.success && preLaunchResponse.data?.enabled;
  } catch (error) {
    console.error('Failed to check pre-launch mode:', error);
  }

  // If pre-launch mode, redirect to coming soon (local token check only)
  if (isPreLaunchMode && !isTokenLikelyValid()) {
    return redirect('/coming-soon');
  }

  // Otherwise redirect to home (404 behavior)
  return redirect('/');
}

/**
 * Login page loader
 * Redirects to home if already authenticated
 */
async function loginLoader() {
  const _user = await getAuthenticatedUser();

  if (_user) {
    return redirect('/');
  }

  // Check if we're in full pre-launch mode (global site pre-launch)
  let isPreLaunchMode = false;
  try {
    const preLaunchResponse = await apiRequest('/api/v1/pre-launch/config');
    isPreLaunchMode = preLaunchResponse.success && preLaunchResponse.data?.enabled;
  } catch (error) {
    console.error('Failed to check pre-launch mode:', error);
  }

  return { _user: null, isPreLaunchMode };
}

// ============================================================================
// ROUTE CONFIGURATION
// ============================================================================

/**
 * Application routes with loaders
 *
 * Route structure:
 * - path: URL pattern
 * - element: Component to render
 * - loader: Data loading function (runs before render)
 * - errorElement: Error boundary component (optional)
 *
 * TESTING CHECKLIST:
 * ────────────────────────────────────────────────────────────────
 * [ ] Public routes work without authentication
 * [ ] /login redirects authenticated users to home
 * [ ] /admin/ab-tests redirects non-authenticated to /login
 * [ ] /admin/ab-tests redirects non-admin users to /unauthorized
 * [ ] Login preserves redirect destination (try /admin/ab-tests → login → back to /admin/ab-tests)
 * [ ] super_admin and staff can access /admin/ab-tests
 * [ ] customer role cannot access /admin/ab-tests
 * [ ] Home page loads search results without loading spinner
 * [ ] Error pages (/unauthorized) show proper messaging
 *
 * FUTURE ROUTES TO ADD:
 * ────────────────────────────────────────────────────────────────
 * - /dashboard (customer dashboard - requireAuthLoader)
 * - /guide/dashboard (guide dashboard - requireRoleLoader(['guide']))
 * - /admin/users (_user management - requireRoleLoader(['admin', 'super_admin']))
 * - /bookings/:id (booking details - requireAuthLoader + ownership check)
 * - /messages (messaging system - requireAuthLoader)
 */
export const routes = [
  // ──────────────────────────────────────────────────────────────────────
  // PUBLIC ROUTES (No authentication required)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/',
    element: <HomePage />,
    loader: withPreLaunchProtection(homeLoader),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/coming-soon',
    element: <ComingSoonPage />,
    loader: optionalAuthLoader,
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // PRE-LAUNCH EMAIL CONFIRMATION ROUTES (MVP 1.23)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/confirm-email-prelaunch',
    element: <ConfirmEmailPreLaunchPage />,
    errorElement: <ErrorBoundary />
  },


  {
    path: '/experience/:experienceId',
    element: <ExperienceDetail />,
    loader: experienceDetailLoader,
    hydrateFallbackElement: <HydrateFallback />,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/experience2/:experienceId',
    element: <ExperienceDetailStatic />,
    loader: experienceDetailLoader,
    hydrateFallbackElement: <HydrateFallback />,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/schedule/:marketSlug',
    element: <SchedulePage />,
    loader: optionalAuthLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guides/:guideId',
    element: <GuideProfilePage />,
    loader: withPreLaunchProtection(guideProfileLoader),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guides/signup',
    element: <PreLaunchExemptRoute><GuideSignupFlow /></PreLaunchExemptRoute>,
    loader: withPreLaunchProtection(optionalAuthLoader),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guides/verification',
    element: <PreLaunchExemptRoute><GuideVerification /></PreLaunchExemptRoute>,
    loader: withPreLaunchProtection(optionalAuthLoader),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guides/signup-success',
    element: <PreLaunchExemptRoute><GuideSignupSuccess /></PreLaunchExemptRoute>,
    loader: withPreLaunchProtection(optionalAuthLoader),
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // STATIC CONTENT PAGES (Footer links - MVP 1.31)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/help',
    element: <StaticPage />,
    loader: staticPageLoader('help'),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/safety',
    element: <StaticPage />,
    loader: staticPageLoader('safety'),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/cancellation-options',
    element: <StaticPage />,
    loader: staticPageLoader('cancellation'),
    errorElement: <ErrorBoundary />
  },

  // MVP 1.40.3 (SCRUM-2203): Legal documents are served from the DB via the
  // public API (current published version by slug). /eula and /guide-agreement
  // render a "not available yet" placeholder until a super-admin publishes v1.0.
  {
    path: '/privacy',
    element: <LegalDocumentPage slug="privacy-policy" />,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/terms',
    element: <LegalDocumentPage slug="terms-of-service" />,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/eula',
    element: <LegalDocumentPage slug="eula" />,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide-agreement',
    element: <LegalDocumentPage slug="guide-agreement" />,
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // INTENT RESPONSE ROUTE (Public — no auth required, MVP 1.39.1)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/i/:intentCode',
    element: <PreLaunchExemptRoute><IntentResponsePage /></PreLaunchExemptRoute>,
    loader: withPreLaunchProtection(() => null),
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // AUTHENTICATION ROUTES
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/login',
    element: <LoginPage />,
    loader: loginLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
    errorElement: <ErrorBoundary />
  },

  {
    // SCRUM-2260: set-initial-password page for OAuth-only users
    path: '/account/set-password',
    element: <SetPasswordPage />,
    errorElement: <ErrorBoundary />
  },

  // SCRUM-2266: public deletion-status page (Meta App Review gate)
  {
    path: '/deletion-status',
    element: <DeletionStatusPage />,
    errorElement: <ErrorBoundary />
  },

  // SCRUM-2267: public Data Deletion Instructions page (Meta App Review gate +
  // public reference for non-account holders). SCRUM-2271 absorbed this into
  // the versioned legal-doc system; URL is unchanged (Meta-registered).
  {
    path: '/data-deletion',
    element: <LegalDocumentPage slug="data-deletion-instructions" />,
    errorElement: <ErrorBoundary />
  },

  // SCRUM-2266: reclaim flow — both link forms route to the same page.
  {
    path: '/account/reclaim',
    element: <ReclaimAccountPage />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/account/cancel-deletion',
    element: <ReclaimAccountPage />,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/auth/callback',
    element: <AuthCallbackPage />,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/unauthorized',
    element: <UnauthorizedPage />,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/service-unavailable',
    element: <ServiceUnavailablePage />,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/rate-limited',
    element: <RateLimitPage />,
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // PROTECTED ROUTES (Authentication required)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/booking/:holdId',
    element: <BookingFlowPage />,
    loader: withPreLaunchProtection(optionalAuthLoader),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/booking/confirmation',
    element: <BookingConfirmationPage />,
    loader: withPreLaunchProtection(optionalAuthLoader),
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // ADMIN LOGIN (public — no auth required, SCRUM-1721)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/admin/login',
    element: <AdminLoginPage />,
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // ADMIN ROUTES (Role-based protection)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/admin/dashboard',
    element: <AdminDashboard />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    // SCRUM-1798: admin self-service settings (ops alert preferences)
    // SCRUM-1803: on-call schedule card added on this page
    path: '/admin/settings',
    element: <AdminSettings />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    // SCRUM-1803: super_admin read-only overview of all admins' on-call schedules
    path: '/admin/settings/on-call-overview',
    element: <OnCallOverview />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    // SCRUM-2176: real-time system health dashboard (MVP 1.29.5-05)
    path: '/admin/system-health',
    element: <SystemHealthDashboard />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/users',
    element: <UserManagement />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/guide-approvals',
    element: <GuideApprovalQueue />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/analytics',
    element: <PlatformAnalyticsDashboard />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/ab-tests',
    element: <ABTestDashboard />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/referrals',
    element: <ReferralDashboard />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/accounting',
    element: <AdminAccounting />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/errors',
    element: <ErrorsDashboard />,
    loader: errorsDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/services-usage',
    element: <ServicesUsageDashboard />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/message-queue',
    element: <MessageQueueDashboard />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/species-catalog',
    element: <SpeciesCatalogAdmin />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/gear-catalog',
    element: <GearCatalogAdmin />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/review-moderation',
    element: <ReviewModerationQueue />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/bookings',
    element: <BookingManagement />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/cancellations',
    element: <CancellationManagement />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/disputes',
    element: <DisputeManagement />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/dlq',
    element: <DlqMonitoring />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/refunds',
    element: <RefundManagement />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/stuck-refunds',
    element: <StuckRefunds />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/experiences',
    element: <ExperienceManagement />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/leads',
    element: <LeadManagement />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/lead-actions',
    element: <LeadActionFeed />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/markets',
    element: <MarketsPanel />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/intent-codes',
    element: <IntentCodePanel />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/marketing/campaigns',
    element: <CampaignManagement />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/marketing/campaigns/:id',
    element: <CampaignDetail />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/marketing/intent-types',
    element: <IntentTypeManagement />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    // SCRUM-2204: MVP 1.40.4 — super-admin-only legal document authoring surface.
    path: '/admin/legal-documents',
    element: <LegalDocumentManagement />,
    loader: requireRoleLoader(['super_admin']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/marketing/events',
    element: <EventsPanel />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/marketing/events/:id',
    element: <EventDetail />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/marketing/scheduling',
    element: <SchedulingPanel />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/admin/scheduling/callback',
    element: <SchedulingOAuthCallback />,
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // SUPPORT ROUTES (Require admin or support role)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/support/dashboard',
    element: <SupportDashboard />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/support/queue',
    element: <SupportDashboard />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/support/reports',
    element: <SupportReports />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/support/ticket/:ticketId',
    element: <SupportTicketDetail />,
    loader: adminDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // GUIDE ROUTES (Require 'guide' role)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/guide/dashboard',
    element: <GuideDashboard />,
    loader: guideDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/bookings',
    element: <GuideBookings />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/bookings/:bookingId',
    element: <GuideBookingDetail />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/availability',
    element: <GuideAvailabilityPage />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/reviews',
    element: <GuideReviews />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/experiences',
    element: <GuideExperiences />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/experiences/new',
    element: <CreateExperiencePage />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/experiences/:experienceId/edit',
    element: <EditExperiencePage />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/experiences/:experienceId/pricing',
    element: <ExperiencePricingPage />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/earnings',
    element: <RevenueDashboard />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/earnings/transactions',
    element: <TransactionHistory />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/earnings/referrals',
    element: <ReferralTracking />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/earnings/export',
    element: <LedgerExportPage />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/dashboard/settings/payments',
    element: <PaymentSettings />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  // SCRUM-1170: Guide settings — integrations tab (MVP 1.12.4 C-11)
  // Accessible at /guide/settings?tab=integrations
  {
    path: '/guide/settings',
    element: <AccountSettings />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/earnings/tax-documents',
    element: <TaxDocuments />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/accounting',
    element: <AccountingPage />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/messages',
    element: <Messages />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/guide/profile',
    element: <ProfileEditor />,
    loader: requireRoleLoader(['guide']),
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // CUSTOMER ROUTES (Require 'customer' role)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/customer/dashboard',
    element: <CustomerDashboard />,
    loader: customerDashboardLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/customer/trips',
    element: <CustomerTrips />,
    loader: requireRoleLoader(['customer']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/customer/trips/:bookingId',
    element: <CustomerTripDetail />,
    loader: requireRoleLoader(['customer']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/customer/messages',
    element: <CustomerMessages />,
    loader: requireRoleLoader(['customer']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/customer/reviews',
    element: <CustomerReviews />,
    loader: requireRoleLoader(['customer']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/customer/reviews/new',
    element: <CustomerReviewWizard />,
    loader: requireRoleLoader(['customer']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/customer/reviews/edit/:reviewId',
    element: <CustomerReviewEdit />,
    loader: requireRoleLoader(['customer']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/customer/favorites',
    element: <CustomerFavorites />,
    loader: requireRoleLoader(['customer']),
    errorElement: <ErrorBoundary />
  },

  {
    path: '/account/payments',
    element: <PaymentHistory />,
    loader: requireRoleLoader(['customer']),
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // SHARED AUTHENTICATED ROUTES (All _user types)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '/notifications',
    element: <NotificationsPage />,
    loader: requireAuthLoader,
    errorElement: <ErrorBoundary />
  },

  {
    path: '/account/settings',
    element: <AccountSettings />,
    loader: requireAuthLoader,
    errorElement: <ErrorBoundary />
  },

  // ──────────────────────────────────────────────────────────────────────
  // CATCH-ALL ROUTE (404 handling)
  // ──────────────────────────────────────────────────────────────────────

  {
    path: '*',
    element: <ComingSoonPage />, // Show coming soon page for undefined routes
    loader: catchAllLoader, // Handles pre-launch redirect logic
    errorElement: <ErrorBoundary />
  }
];

export default routes;
